import { useState } from 'react';
import { api, errorMessage } from '../../api';
import { fmt } from '../../format';
import { useSession } from '../../session';
import { Empty, ErrorBanner, Modal, Spinner, useLoad, useToast } from '../../ui';
import { Icon } from '../../ui/icons';

interface ApiKeyView {
  readonly id: string;
  readonly label: string;
  readonly source: string;
  readonly warehouseCode: string;
  readonly prefix: string;
  readonly createdAt: string;
  readonly lastUsedAt: string | null;
  readonly revokedAt: string | null;
}

/**
 * Keys of the seller's own systems (the web shop, a Wix site): orders they send to
 * POST /api/v1/orders/inbound arrive as manual orders, without anyone typing them in; a full refund on the
 * shop cancels its order at the warehouse (POST /api/v1/orders/<number>/cancel).
 */
export function ApiKeys() {
  const { me } = useSession();
  const toast = useToast();
  const list = useLoad(() => api<ApiKeyView[]>('GET', '/api/integrations/api-keys'), []);
  const [creating, setCreating] = useState(false);
  const [shown, setShown] = useState<{ label: string; key: string } | null>(null);
  const owner = me.role === 'owner';
  const endpoint = `${window.location.origin}/api/v1/orders/inbound`;

  const revoke = async (k: ApiKeyView) => {
    if (!window.confirm(`${k.label} anahtarı iptal edilsin mi? Bu anahtarla gelen istekler hemen reddedilir.`)) return;
    try {
      await api('POST', `/api/integrations/api-keys/${k.id}/revoke`);
      toast('Anahtar iptal edildi', 'ok');
      void list.reload();
    } catch (e) {
      toast(errorMessage(e), 'bad');
    }
  };

  return (
    <section className="card">
      <div className="card-h">
        <Icon name="key" />
        <h2>Kendi siteniz ve diğer sistemler</h2>
        <span className="sub">Sitenize gelen siparişler API ile otomatik aktarılır</span>
        <span className="spacer" />
        <button className="btn sm" disabled={!owner} onClick={() => setCreating(true)}>
          <Icon name="plus" size={16} /> API anahtarı oluştur
        </button>
      </div>
      <div className="card-b stack">
        <p className="muted" style={{ margin: 0, fontSize: 13.5 }}>
          Siteniz yeni siparişi <span className="mono">{endpoint}</span> adresine gönderir; sipariş manuel sipariş olarak depoya düşer. Sipariş durumu ve
          kargo takip numarası <span className="mono">/api/v1/orders/&lt;sipariş no&gt;</span> adresinden okunur. Tam iade edilen sipariş{' '}
          <span className="mono">/api/v1/orders/&lt;sipariş no&gt;/cancel</span> ile depoda iptal edilir. Ayrıntı ve örnekler: docs/siparis-api.md.
        </p>
        {list.error && <ErrorBanner message={list.error} onRetry={() => void list.reload()} />}
        {!list.data ? (
          <Spinner />
        ) : list.data.length === 0 ? (
          <Empty title="Henüz API anahtarı yok">asfaltmarket gibi kendi siteniz için bir anahtar oluşturun.</Empty>
        ) : (
          <div className="table-wrap">
            <table className="t">
              <thead>
                <tr>
                  <th>Anahtar</th>
                  <th>Sipariş kaynağı</th>
                  <th>Depo</th>
                  <th>Oluşturuldu</th>
                  <th>Son kullanım</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {list.data.map((k) => (
                  <tr key={k.id} className={k.revokedAt ? 'muted-row' : ''}>
                    <td>
                      <b>{k.label}</b>
                      <div className="mono muted">{k.prefix}…</div>
                    </td>
                    <td>{k.source}</td>
                    <td className="mono">{k.warehouseCode}</td>
                    <td>{fmt.dateTime(k.createdAt)}</td>
                    <td>{k.lastUsedAt ? fmt.dateTime(k.lastUsedAt) : '—'}</td>
                    <td>
                      {k.revokedAt ? (
                        <span className="chip">İptal edildi</span>
                      ) : (
                        <button className="btn sm ghost danger-text" disabled={!owner} onClick={() => void revoke(k)}>
                          İptal et
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!owner && <div className="banner info">API anahtarını yalnızca hesap sahibi oluşturur ve iptal eder.</div>}
      </div>
      {creating && (
        <NewKey
          onClose={() => setCreating(false)}
          onCreated={(made) => {
            setCreating(false);
            setShown(made);
            void list.reload();
          }}
        />
      )}
      {shown && (
        <Modal
          title={`${shown.label} · API anahtarı`}
          onClose={() => setShown(null)}
          footer={
            <button className="btn primary" onClick={() => setShown(null)}>
              Kaydettim, kapat
            </button>
          }
        >
          <div className="stack">
            <div className="banner warn">Bu anahtar yalnızca şimdi gösterilir. Sitenizin gizli ayarlarına (ortam değişkeni) kaydedin; kimseyle paylaşmayın.</div>
            <div className="big-code" style={{ wordBreak: 'break-all' }}>
              {shown.key}
            </div>
            <div className="row">
              <button
                className="btn sm"
                onClick={() =>
                  void navigator.clipboard
                    ?.writeText(shown.key)
                    .then(() => toast('Anahtar kopyalandı', 'ok'))
                    .catch(() => toast('Kopyalanamadı; anahtarı seçip kopyalayın', 'bad'))
                }
              >
                Kopyala
              </button>
            </div>
            <p className="muted" style={{ fontSize: 13 }}>
              İsteklerde <span className="mono">Authorization: Bearer {shown.key.slice(0, 12)}…</span> başlığıyla kullanılır. Anahtar ele geçerse buradan iptal
              edip yenisini oluşturun.
            </p>
          </div>
        </Modal>
      )}
    </section>
  );
}

function NewKey({ onClose, onCreated }: { onClose: () => void; onCreated: (made: { label: string; key: string }) => void }) {
  const { warehouses } = useSession();
  const toast = useToast();
  const [label, setLabel] = useState('');
  const [source, setSource] = useState('');
  const [warehouseId, setWarehouseId] = useState(warehouses[0]?.id ?? '');
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const made = await api<{ label: string; key: string }>('POST', '/api/integrations/api-keys', { label: label.trim(), source: source.trim(), warehouseId });
      onCreated(made);
    } catch (e) {
      toast(errorMessage(e), 'bad');
      setBusy(false);
    }
  };
  return (
    <Modal
      title="Yeni API anahtarı"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Vazgeç
          </button>
          <button className="btn primary" disabled={busy || !label.trim() || !source.trim() || !warehouseId} onClick={() => void save()}>
            Oluştur
          </button>
        </>
      }
    >
      <div className="form-grid">
        <div className="field">
          <label htmlFor="key-label">Anahtarın adı</label>
          <input id="key-label" className="input" value={label} maxLength={80} onChange={(e) => setLabel(e.target.value)} placeholder="asfaltmarket sitesi" />
        </div>
        <div className="field">
          <label htmlFor="key-source">Sipariş kaynağı</label>
          <input id="key-source" className="input" value={source} maxLength={40} onChange={(e) => setSource(e.target.value)} placeholder="asfaltmarket" />
          <span className="hint">Siparişlerde “kaynak” olarak görünür</span>
        </div>
        {warehouses.length > 1 && (
          <div className="field">
            <label htmlFor="key-wh">Siparişlerin gideceği depo</label>
            <select id="key-wh" className="select" value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)}>
              {warehouses.map((w) => (
                <option key={w.id} value={w.id}>
                  {w.code} · {w.name}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>
    </Modal>
  );
}
